import React, { ReactNode, forwardRef } from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';

interface AnimatedButtonProps extends Omit<HTMLMotionProps<'button'>, 'ref'> {
  variant?: 'primary' | 'secondary' | 'outlined' | 'outline' | 'text' | 'ghost' | 'danger' | 'success' | 'warning' | 'info' | 'filled';
  color?: 'primary' | 'secondary' | 'danger' | 'red' | 'gray';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  isLoading?: boolean;
  loading?: boolean;
  loadingText?: string;
  animationPreset?: 'spring' | 'gentle' | 'bouncy' | 'smooth';
}

const AnimatedButton = forwardRef<HTMLButtonElement, AnimatedButtonProps>((
  {
    children,
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    leftIcon,
    rightIcon,
    isLoading = false,
    loading = false,
    loadingText,
    disabled,
    className = '',
    animationPreset = 'spring',
    ...props
  }, 
  ref
) => {
  const isButtonLoading = isLoading || loading;
  
  // Animation variants
  const animationVariants = {
    spring: {
      whileHover: { scale: 1.02 },
      whileTap: { scale: 0.98 },
      transition: { type: 'spring' as const, stiffness: 400, damping: 17 }
    },
    gentle: {
      whileHover: { scale: 1.01 },
      whileTap: { scale: 0.99 },
      transition: { type: 'spring' as const, stiffness: 300, damping: 20 }
    },
    bouncy: {
      whileHover: { scale: 1.05 },
      whileTap: { scale: 0.95 },
      transition: { type: 'spring' as const, stiffness: 500, damping: 15 }
    },
    smooth: {
      whileHover: { scale: 1.02 },
      whileTap: { scale: 0.98 },
      transition: { duration: 0.2, ease: 'easeInOut' as const }
    }
  };

  // Base styles
  let baseStyle = 'inline-flex items-center justify-center rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors';
  
  // Get the appropriate color based on color prop
  const getColorStyle = (color?: string, variant?: string) => {
    if (color) {
      const normalizedVariant = variant === 'filled' ? 'primary' : variant;
      
      switch (color) {
        case 'primary':
          return normalizedVariant === 'outlined' || normalizedVariant === 'outline' 
            ? 'border-red-600 text-red-600 hover:bg-red-50 focus:ring-red-500' 
            : 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500';
        case 'secondary':
          return normalizedVariant === 'outlined' || normalizedVariant === 'outline' 
            ? 'border-blue-600 text-blue-600 hover:bg-blue-50 focus:ring-blue-500' 
            : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500';
        case 'danger':
          return normalizedVariant === 'outlined' || normalizedVariant === 'outline'
            ? 'border-error text-error hover:bg-red-50 focus:ring-error' 
            : 'bg-error text-white hover:bg-error-800 focus:ring-error';
        case 'red':
          return normalizedVariant === 'outlined' || normalizedVariant === 'outline'
            ? 'border-red-600 text-red-600 hover:bg-red-50 focus:ring-red-500' 
            : 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500';
        case 'gray':
          return normalizedVariant === 'outlined' || normalizedVariant === 'outline'
            ? 'border-gray-500 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-gray-400' 
            : 'bg-gray-500 text-white hover:bg-gray-600 focus:ring-gray-400';
        default:
          return '';
      }
    }
    return '';
  };
  
  // Variant styles
  const variantStyles = {
    primary: 'bg-red text-white hover:bg-red-800 focus:ring-red',
    secondary: 'bg-blue text-white hover:bg-blue-800 focus:ring-blue',
    filled: 'bg-red text-white hover:bg-red-800 focus:ring-red',
    outlined: 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-blue',
    outline: 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-blue',
    text: 'text-blue dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 focus:ring-blue',
    ghost: 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:ring-gray-400',
    danger: 'bg-error text-white hover:bg-error-800 focus:ring-error',
    success: 'bg-success text-success-text hover:opacity-90 focus:ring-success',
    warning: 'bg-warning text-warning-text hover:opacity-90 focus:ring-warning',
    info: 'bg-info text-info-text hover:opacity-90 focus:ring-info'
  };
  
  // Size styles
  const sizeStyles = {
    sm: 'px-3 py-2.5 text-sm min-h-[44px]',
    md: 'px-4 py-3 text-base min-h-[44px]',
    lg: 'px-6 py-3.5 text-lg min-h-[48px]'
  };
  
  const widthStyle = fullWidth ? 'w-full' : '';
  const disabledStyle = disabled || isButtonLoading ? 'opacity-60 cursor-not-allowed' : '';
  
  const styleToUse = props.color ? getColorStyle(props.color, variant) : variantStyles[variant];
  const combinedClassName = `${baseStyle} ${styleToUse} ${sizeStyles[size]} ${widthStyle} ${disabledStyle} ${className}`;
  
  const currentAnimation = animationVariants[animationPreset];

  return (
    <motion.button
      ref={ref}
      className={combinedClassName}
      disabled={disabled || isButtonLoading}
      {...currentAnimation}
      {...props}
    >
      {isButtonLoading ? (
        <>
          <motion.svg 
            className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </motion.svg>
          {loadingText || children}
        </>
      ) : (
        <>
          {leftIcon && (
            <motion.span 
              className="mr-2"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              {leftIcon}
            </motion.span>
          )}
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.05 }}
          >
            {children}
          </motion.span>
          {rightIcon && (
            <motion.span 
              className="ml-2"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              {rightIcon}
            </motion.span>
          )}
        </>
      )}
    </motion.button>
  );
});

AnimatedButton.displayName = 'AnimatedButton';

export default AnimatedButton;